<template>
  <div class="space-y-6">
    <!-- Alerts -->
    <UiKitAlert v-if="errorMessage" color="danger">{{ errorMessage }}</UiKitAlert>
    <UiKitAlert v-if="successMessage" color="success">{{ successMessage }}</UiKitAlert>

    <!-- Form -->
    <form class="space-y-4" @submit.prevent="saveSettings">
      <UiKitInput
        v-model="form.INN"
        label="ИНН"
        placeholder="Введите ИНН"
      />

      <UiKitInput
        v-model="form.OGRN"
        label="ОГРН"
        placeholder="Введите ОГРН"
      />

      <UiKitInput
        v-model="form.legal_address"
        label="Юридический адрес"
        placeholder="Введите юридический адрес"
      />

      <UiKitInput
        v-model="form.individual_entrepreneur"
        label="Индивидуальный предприниматель"
        placeholder="Введите данные ИП"
      />

      <div class="flex justify-end gap-3 pt-4 border-t border-hui-divider">
        <UiKitButton type="button" color="default" variant="flat" @click="closeModal">
          Отмена
        </UiKitButton>
        <UiKitButton type="submit" color="primary" :loading="loading" :disabled="loading">
          Сохранить настройки
        </UiKitButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola'
import { ref, onMounted } from 'vue'
import { APIAdminInstitution } from '~/fsd-pages/admin/api'
import { useModalStore } from '@shared/model'
import { useKitToast } from '@shared/ui/kit'
import type { LegalSettings } from '@entities/institution'

const emit = defineEmits<{
  'update:size': [size: string]
}>()

const modalStore = useModalStore()
const { toast: kitToast } = useKitToast()
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({
      color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default',
      title: o.title,
      description: o.description,
      timeout: o.timeout,
    }),
}

const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const form = ref<LegalSettings>({
  INN: '',
  OGRN: '',
  legal_address: '',
  individual_entrepreneur: '',
})

const loadSettings = async () => {
  try {
    loading.value = true
    const response = await APIAdminInstitution.getLegalSettings()

    // Данные приходят в объекте legal
    const settings = response.legal || response

    form.value = { ...settings }
  } catch (error) {
    consola.error('Ошибка загрузки юридической информации:', error)
    errorMessage.value = 'Ошибка загрузки настроек'
  } finally {
    loading.value = false
  }
}

const saveSettings = async () => {
  try {
    loading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    await APIAdminInstitution.updateLegalSettings(form.value)
    successMessage.value = 'Юридическая информация успешно сохранена'

    toast.add({ title: 'Сохранено', description: 'Юридическая информация успешно сохранена', color: 'green', timeout: 3000 })

    setTimeout(() => {
      closeModal()
    }, 1500)
  } catch (error) {
    consola.error('Ошибка сохранения юридической информации:', error)
    errorMessage.value = 'Ошибка сохранения настроек'
    toast.add({ title: 'Ошибка', description: 'Не удалось сохранить настройки', color: 'red', timeout: 5000 })
  } finally {
    loading.value = false
  }
}

const closeModal = () => {
  modalStore.close()
}

onMounted(() => {
  emit('update:size', 'l')
  loadSettings()
})
</script>
